import { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { Autocomplete, Box, Checkbox, Chip, ListItemText, TextField } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { UserContext } from '../../../components/shared/useUser';
import { Franchise, Store, Tenant } from '../../../app/services/api/generated';

/**
 * The store scope a report should run for. `tenantId` and `franchiseId` are always set (single
 * selection). `storeNumbers` empty means "all stores of the franchise" — the backend resolves the
 * franchise's accessible stores; a non-empty list is an explicit store sub-selection.
 */
export interface StoreScope {
  tenantId: number;
  franchiseId: number;
  storeNumbers: string[];
}

interface StoreScopePickerProps {
  onChange: (scope: StoreScope | null) => void;
  /** Current scope; the pickers sync to it (e.g. when a quick scope chip sets it). */
  value?: StoreScope | null;
}

const ALL_STORES = '__all_stores__';

/**
 * Standard tenant → franchise → store picker for Reports V2. Single tenant and single franchise,
 * multi-select stores. Franchise/store options are access-scoped from the current user and cascade:
 * tenant filters franchises, franchise filters stores. Defaults to the user's logged-in
 * tenant/franchise with "All Stores" selected; picking an individual store replaces the
 * "All Stores" chip, and clearing the selection falls back to "All Stores".
 */
const StoreScopePicker = ({ onChange, value }: StoreScopePickerProps) => {
  const { t } = useTranslation('common');
  const { user, selectedStore, selectedFranchise } = useContext(UserContext);

  const tenants = useMemo<Tenant[]>(() => user?.tenants ?? [], [user]);
  const franchises = useMemo<Franchise[]>(() => user?.franchises?.filter((f) => f.active !== false) ?? [], [user]);
  const stores = useMemo<Store[]>(() => user?.stores ?? [], [user]);

  const showTenant = tenants.length > 1;

  // Initialise from an externally-supplied scope (e.g. a quick scope chip) when present, so a picker
  // mounted inside the collapsed Advanced filters shows the current selection immediately.
  const [tenant, setTenant] = useState<Tenant | null>(() =>
    value?.tenantId != null ? tenants.find((tn) => tn.id === value.tenantId) ?? null : null);
  const [franchise, setFranchise] = useState<Franchise | null>(() =>
    value ? franchises.find((f) => f.franchiseId === value.franchiseId) ?? null : null);
  const [selectedStores, setSelectedStores] = useState<Store[]>(() =>
    value ? stores.filter((s) => value.storeNumbers.includes(s.storeNumber ?? '')) : []);

  // Sync the pickers when the scope changes externally (e.g. a quick scope chip clicked while the
  // picker is mounted). Skips the very first render — initial state above already reflects `value`.
  const isInitialMount = useRef(true);
  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }
    if (!value) {
      return;
    }
    const nextFranchise = franchises.find((f) => f.franchiseId === value.franchiseId) ?? null;
    const nextTenant = value.tenantId != null ? tenants.find((tn) => tn.id === value.tenantId) ?? null : null;
    const nextStores = stores.filter((s) => value.storeNumbers.includes(s.storeNumber ?? ''));
    setTenant(nextTenant);
    setFranchise(nextFranchise);
    setSelectedStores(nextStores);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  const tenantFranchises = useMemo(
    () => (tenant ? franchises.filter((f) => f.tenantId === tenant.id) : franchises),
    [franchises, tenant],
  );

  const franchiseStores = useMemo(
    () => (franchise ? stores.filter((s) => s.franchiseId === franchise.franchiseId) : []),
    [stores, franchise],
  );

  // Build the scope for a given selection set, or null when it isn't complete yet.
  const buildScope = (nextTenant: Tenant | null, nextFranchise: Franchise | null, nextStores: Store[]): StoreScope | null => {
    if (nextFranchise?.franchiseId == null) {
      return null;
    }
    const tenantId = nextTenant?.id ?? nextFranchise.tenantId;
    if (tenantId == null) {
      return null;
    }
    return {
      tenantId,
      franchiseId: nextFranchise.franchiseId,
      storeNumbers: nextStores.map((s) => s.storeNumber).filter((n): n is string => !!n),
    };
  };

  // Emit only on explicit user actions (never from an effect) to avoid an onChange/setState loop.
  // Emits null when the selection is incomplete (e.g. franchise cleared) so the page clears the
  // scope and forms disable Generate — otherwise a stale scope would linger and run reports wrongly.
  const emit = (nextTenant: Tenant | null, nextFranchise: Franchise | null, nextStores: Store[]) => {
    const scope = buildScope(nextTenant, nextFranchise, nextStores);
    onChange(scope);
  };

  // Default to the logged-in tenant/franchise once user data is available, and emit once.
  // Skip when a scope was already supplied externally (e.g. a quick scope chip) — the value-sync
  // effect hydrates the pickers from it instead, so we don't clobber it with the default.
  useEffect(() => {
    if (tenant || !franchises.length || value) {
      return;
    }

    const defaultFranchise =
      (selectedFranchise && franchises.find((f) => f.franchiseId === selectedFranchise.franchiseId)) ||
      (selectedStore && franchises.find((f) => f.franchiseId === selectedStore.franchiseId)) ||
      (franchises.length === 1 ? franchises[0] : null);

    if (!defaultFranchise) {
      return;
    }

    const defaultTenant = showTenant
      ? tenants.find((tn) => tn.id === defaultFranchise.tenantId) ?? null
      : tenants[0] ?? null;

    setFranchise(defaultFranchise);
    setTenant(defaultTenant);
    emit(defaultTenant, defaultFranchise, []);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [franchises.length]);

  const handleTenantChange = (next: Tenant | null) => {
    setTenant(next);
    setFranchise(null);
    setSelectedStores([]);
    // Changing or clearing the tenant leaves the franchise unselected — scope is incomplete until a
    // franchise is picked, so clear the page's scope (and block Generate) in the meantime.
    onChange(null);
  };

  const handleFranchiseChange = (next: Franchise | null) => {
    setFranchise(next);
    setSelectedStores([]);
    const nextTenant = next && showTenant ? tenants.find((tn) => tn.id === next.tenantId) ?? null : tenant;
    if (next && showTenant) {
      setTenant(nextTenant);
    }
    emit(nextTenant, next, []);
  };

  const handleStoreChange = (next: Store[]) => {
    // Selecting "All Stores" clears any explicit selection back to the whole franchise.
    const nextStores = next.some((s) => s.storeNumber === ALL_STORES) ? [] : next;
    setSelectedStores(nextStores);
    emit(tenant, franchise, nextStores);
  };

  // Synthetic first option representing "All Stores" so the dropdown can toggle back to it.
  const allStoresOption: Store = {
    storeNumber: ALL_STORES,
    storeName: t('reportingV2.all-stores'),
  };
  const storeOptions = useMemo(
    () => [allStoresOption, ...franchiseStores],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [franchiseStores],
  );

  return (
    <Box sx={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
      {showTenant && (
        <Autocomplete
          sx={{ minWidth: '220px', flex: 1 }}
          options={tenants}
          value={tenant}
          getOptionLabel={(option) => option.name ?? ''}
          isOptionEqualToValue={(option, value) => option.id === value.id}
          onChange={(_e, value) => handleTenantChange(value)}
          renderInput={(params) => (
            <TextField {...params}
              size="small"
              label={t('reportingV2.tenant')} />
          )}
        />
      )}

      <Autocomplete
        sx={{ minWidth: '220px', flex: 1 }}
        options={tenantFranchises}
        value={franchise}
        getOptionLabel={(option) => option.franchiseName ?? ''}
        isOptionEqualToValue={(option, value) => option.franchiseId === value.franchiseId}
        onChange={(_e, value) => handleFranchiseChange(value)}
        renderInput={(params) => (
          <TextField {...params}
            size="small"
            label={t('reportingV2.franchise')} />
        )}
      />

      <Autocomplete
        multiple
        disableCloseOnSelect
        limitTags={2}
        sx={{ minWidth: '260px', flex: 2 }}
        options={storeOptions}
        value={selectedStores}
        disabled={!franchise}
        getOptionLabel={(option) =>
          option.storeNumber === ALL_STORES
            ? t('reportingV2.all-stores')
            : `${option.storeName} (${option.storeNumber})`
        }
        isOptionEqualToValue={(option, value) => option.storeNumber === value.storeNumber}
        onChange={(_e, value) => handleStoreChange(value)}
        renderOption={(props, option) => {
          const isAllStores = option.storeNumber === ALL_STORES;
          const selected = isAllStores ? selectedStores.length === 0 : selectedStores.some((s) => s.storeNumber === option.storeNumber);
          return (
            <li {...props}
              key={option.storeNumber}>
              <Checkbox checked={selected}
                sx={{ mr: 1 }} />
              <ListItemText primary={isAllStores ? t('reportingV2.all-stores') : option.storeName}
                secondary={isAllStores ? undefined : option.storeNumber} />
            </li>
          );
        }}
        renderTags={(value, getTagProps) =>
          value.map((store, index) => (
            <Chip
              {...getTagProps({ index })}
              key={store.storeNumber}
              label={`${store.storeName} (${store.storeNumber})`}
              size="small"
            />
          ))
        }
        renderInput={(params) => (
          <TextField {...params}
            size="small"
            label={t('reportingV2.stores')}
            InputProps={{
              ...params.InputProps,
              // MUI doesn't call renderTags when the selection is empty, so render the
              // "All Stores" chip as a start adornment instead (it is the default state).
              startAdornment: (
                <>
                  {selectedStores.length === 0 && franchise && (
                    <Chip
                      key={ALL_STORES}
                      label={t('reportingV2.all-stores')}
                      size="small"
                      sx={{ mr: '4px' }}
                    />
                  )}
                  {params.InputProps.startAdornment}
                </>
              ),
            }} />
        )}
        noOptionsText={t('reportingV2.no-stores-found')}
      />
    </Box>
  );
};

export default StoreScopePicker;
